import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Heart, Brain, Shield, ArrowRight, Calendar, CheckCircle } from 'lucide-react';
import CTA from '../components/CTA';

const PersistentPain: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Persistent Pain Treatment with Clinical Hypnotherapy | Belfast, Lisburn & Northern Ireland</title>
        <meta
          name="description"
          content="Neuroscience-based hypnotherapy to help reduce persistent (chronic) pain by calming the nervous system and supporting recovery. Serving Belfast, Lisburn & Northern Ireland."
        />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/persistent-pain/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Can hypnotherapy really help with chronic pain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is an evidence-based approach for chronic pain management. Research shows it can reduce pain intensity by working with the nervous system to 'close the pain gate' and reduce central sensitization. A major review found hypnotherapy 68% more effective than no treatment for chronic pain."
                }
              },
              {
                "@type": "Question",
                "name": "How does hypnotherapy work for persistent pain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Hypnotherapy works by calming the fight-or-flight response, which keeps pain signals amplified. It uses neuroplasticity to create new, healthier neural pathways, helping your brain interpret sensations differently. This process 'turns down the volume' on pain signals."
                }
              },
              {
                "@type": "Question",
                "name": "How many sessions will I need for chronic pain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The number of sessions varies based on individual circumstances. Most clients see improvement within 6-8 sessions, though some notice changes sooner. We'll create a personalized treatment plan during your initial consultation."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy replace pain medication?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Hypnotherapy is a complementary therapy that works alongside your medical treatment, not as a replacement. Many clients find they can reduce medication under medical supervision, but any changes to medication should always be discussed with your doctor."
                }
              },
              {
                "@type": "Question",
                "name": "What conditions can hypnotherapy help with?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Hypnotherapy can help with various persistent pain conditions including fibromyalgia, chronic back pain, migraines, IBS, arthritis, neuropathic pain, and other forms of chronic pain. It's particularly effective when combined with physiotherapy, movement therapy, or other pain management approaches."
                }
              }
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Chronic Pain Management Hypnotherapy",
            "name": "Persistent Pain Treatment with Clinical Hypnotherapy",
            "description": "Neuroscience-based hypnotherapy to help reduce persistent (chronic) pain by calming the nervous system and supporting recovery. Serving Belfast, Lisburn & Northern Ireland.",
            "provider": {
              "@type": "MedicalBusiness",
              "name": "Sharon Clare Hypnotherapy",
              "telephone": "07932139245",
              "email": "Sharon@sharonclare-hypnotherapy.com",
              "url": "https://www.sharonclare-hypnotherapy.com/",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "addressRegion": "County Antrim",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "areaServed": ["Belfast", "Lisburn", "Northern Ireland"],
            "availableChannel": {
              "@type": "ServiceChannel",
              "serviceUrl": "https://www.sharonclare-hypnotherapy.com/persistent-pain",
              "servicePhone": {
                "@type": "ContactPoint",
                "telephone": "07932139245",
                "contactType": "customer service"
              }
            }
          })}
        </script>
      </Helmet>

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
          <div className="container-custom">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-4">
                Persistent Pain Treatment with Clinical Hypnotherapy
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 mb-6">
                Serving Belfast, Lisburn & Northern Ireland
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/book"
                  className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Request a 10-Minute Call
                </a>
                <a href="tel:07932139245" className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center">
                  <ArrowRight className="w-5 h-5 mr-2" />
                  Call 07932 139245
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Understanding Persistent Pain */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="container-custom max-w-4xl">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              Understanding Persistent (Chronic) Pain
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Persistent pain—often called chronic pain—lasts longer than 12 weeks and affects millions of people worldwide. It can follow an injury, surgery, or illness, but it can also develop without a clear starting point.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Modern neuroscience shows that persistent pain is not “all in your head,” but it is in your nervous system. When pain continues, the brain and spinal cord adapt in ways that keep the alarm switched on, even when tissues have healed.
            </p>

            <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8 md:p-10 mb-8">
              <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">Key mechanisms include:</h3>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <Shield className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700"><strong>Central Sensitisation</strong> – The spinal cord and brain become hypersensitive, amplifying normal signals and interpreting them as pain.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Shield className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700"><strong>Fight–Flight–Freeze Activation</strong> – Ongoing stress keeps your survival system on high alert, which keeps pain signals turned up.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Shield className="w-6 h-6 text-accent-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-700"><strong>Neuroplasticity</strong> – The brain learns pain pathways like a well-worn motorway; the more they’re used, the easier they fire, even with no new injury.</span>
                </li>
              </ul>
              <p className="text-gray-700 mt-6">
                This is why you can feel real, distressing pain even when medical scans look “normal.” The pain is genuine—your brain is simply protecting you a little too well.
              </p>
            </div>
          </div>
        </section>

        {/* How Hypnotherapy Helps */}
        <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
          <div className="container-custom max-w-5xl">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              How Clinical Hypnotherapy Supports Recovery
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center">
              Clinical hypnotherapy is a neuroscience-based therapy that works directly with the mind–body connection to calm the overactive alarm system.
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-start space-x-3 mb-4">
                  <Heart className="w-6 h-6 text-accent-600" />
                  <h3 className="font-heading font-semibold text-gray-900 text-xl">Shift from Danger to Safety</h3>
                </div>
                <p className="text-gray-600">Calms the fight–flight response so your nervous system stops amplifying pain signals.</p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-6 h-6 text-accent-600" />
                  <h3 className="font-heading font-semibold text-gray-900 text-xl">“Close the Pain Gate”</h3>
                </div>
                <p className="text-gray-600">Lowers the intensity of messages travelling through the spinal cord to the brain.</p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-6 h-6 text-accent-600" />
                  <h3 className="font-heading font-semibold text-gray-900 text-xl">Harness Neuroplasticity</h3>
                </div>
                <p className="text-gray-600">Creates new, healthier neural pathways so your brain can learn to interpret sensations differently.</p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-start space-x-3 mb-4">
                  <CheckCircle className="w-6 h-6 text-accent-600" />
                  <h3 className="font-heading font-semibold text-gray-900 text-xl">Practical Tools</h3>
                </div>
                <p className="text-gray-600">Self-hypnosis and relaxation techniques that reduce stress and anxiety, both of which can open the pain gate.</p>
              </div>
            </div>
            <p className="text-gray-600 mt-8">
              Research supports hypnotherapy’s role in multidisciplinary pain care. For example, a major review of over 7,000 children with chronic abdominal pain found hypnotherapy 68% more effective than no treatment—outperforming dietary and medication-based approaches.
            </p>
          </div>
        </section>

        {/* Learn More */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="container-custom max-w-4xl">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6 text-center">Learn More: Read My Brainz Magazine Article</h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-6 text-center">
              For a deeper dive into the neuroscience of persistent pain and how hypnotherapy works, read my detailed Brainz article. Also explore our <a href="/anxiety-stress" className="text-accent-600 hover:text-accent-700 underline">anxiety treatment</a> and <a href="/blog" className="text-accent-600 hover:text-accent-700 underline">hypnotherapy blog</a> for more insights.
            </p>
            <div className="text-center">
              <a
                href="https://www.brainzmagazine.com/executive-contributor/sharon-clare" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center text-accent-600 hover:text-accent-700 font-medium"
              >
                Read the article
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </div>
          </div>
        </section>

        {/* Complementary Care */}
        <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
          <div className="container-custom max-w-5xl">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6 text-center">A Complement to Comprehensive Pain Care</h2>
            <p className="text-lg text-gray-600 leading-relaxed text-center max-w-4xl mx-auto">
              Many chronic pain clinics now recommend hypnotherapy alongside physiotherapy, medication, mindfulness, craniosacral therapy, or gentle movement programs. By addressing the nervous system directly, hypnotherapy enhances these treatments and helps you regain confidence, mobility, and quality of life.
            </p>
          </div>
        </section>

        {/* Locations Section */}
        <section className="py-16 bg-gradient-to-br from-primary-50 to-accent-50">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-gray-900 mb-6">
                Chronic Pain Support Across Northern Ireland
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Access pain management hypnotherapy at three convenient locations:
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                <a href="/belfast-hypnotherapy" className="bg-white rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-2">Belfast</h3>
                  <p className="text-sm text-gray-600 mb-1">Days &amp; times vary — early evening available</p>
                  <p className="text-xs text-gray-500">Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ</p>
                </a>
                <a href="/lisburn-hypnotherapy" className="bg-white rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-2">Lisburn</h3>
                  <p className="text-sm text-gray-600 mb-1">Times vary — early evening available</p>
                  <p className="text-xs text-gray-500">The Arzen Collective, 13B Antrim Street, Lisburn BT28</p>
                </a>
                <a href="/contact" className="bg-white rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <h3 className="font-semibold text-gray-900 mb-2">Online</h3>
                  <p className="text-sm text-gray-600 mb-1">Flexible Scheduling</p>
                  <p className="text-xs text-gray-500">Across Northern Ireland & UK</p>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <CTA
          title="Take the Next Step"
          subtitle="If you’re ready to explore how clinical hypnotherapy can help with persistent pain, book your initial consultation today and start retraining your brain to turn down the volume on pain."
          primaryButtonText="Request a 10-Minute Call"
          primaryButtonHref="/book"
          secondaryButtonText="Call Now"
          secondaryButtonHref="tel:07932139245"
          variant="accent"
        />
      </main>
    </>
  );
};

export default PersistentPain;

